import { Copy } from "lucide-react"
import { toast } from "sonner"

import { usePvpRoomFairnessHashes } from "../hooks/usePvpRoomSelectors"
import { useI18n } from "@/shared/i18n"

const ROW_BG = "bg-[rgba(126,125,155,0.32)]"

interface RoundFairnessHashesProps {
  roomId: string
}

function HashRow({
  label,
  value,
  naLabel,
  onCopy,
  copyAria,
}: {
  label: string
  value: string
  naLabel: string
  onCopy: () => void
  copyAria: string
}) {
  const display = value.trim() ? value : naLabel

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={!value.trim()}
      aria-label={copyAria}
      className={`flex h-8 w-full items-center gap-1.5 rounded-xl ${ROW_BG} px-2.5 text-left transition-opacity active:opacity-90 disabled:cursor-default disabled:active:opacity-100`}
    >
      <span className="shrink-0 text-[11px] font-medium leading-none tracking-[-0.3px] text-white/75">
        {label}:
      </span>
      <span
        className={`min-w-0 flex-1 truncate font-mono text-[11px] leading-none tracking-[-0.2px] ${
          value.trim() ? "text-white/55" : "text-white/35"
        }`}
      >
        {display}
      </span>
      <Copy
        className={`size-3 shrink-0 ${value.trim() ? "text-white/55" : "text-white/25"}`}
        aria-hidden
      />
    </button>
  )
}

export const RoundFairnessHashes = ({ roomId }: RoundFairnessHashesProps) => {
  const { t } = useI18n()
  const { clientCommitHash, saltHash } = usePvpRoomFairnessHashes(roomId)

  const copy = async (value: string) => {
    if (!value.trim()) return
    try {
      await navigator.clipboard.writeText(value)
      toast.success(t("roundDetail.copied"))
    } catch {
      toast.error(t("roundDetail.copyFailed"))
    }
  }

  return (
    <div className="mt-4 w-full border-t border-border/40 pt-3">
      <div className="flex w-full flex-col gap-1.5">
        <HashRow
          label={t("pvpRoom.clientCommitHashLabel")}
          value={clientCommitHash}
          naLabel={t("history.na")}
          onCopy={() => void copy(clientCommitHash)}
          copyAria={t("pvpRoom.copyClientCommitHashAria")}
        />
        <HashRow
          label={t("pvpRoom.saltHashLabel")}
          value={saltHash}
          naLabel={t("history.na")}
          onCopy={() => void copy(saltHash)}
          copyAria={t("pvpRoom.copySaltHashAria")}
        />
      </div>
    </div>
  )
}
